<template>
  <StickyFooter :sticky="true">
    <template #header>
      <div>
        <div class="hero is-info">
          <div class="loader-hero-body">
            <span class="loader-hero-title">Assign Samples</span>
            <ul class="loader-step-checklist">
              <li class="mr-2" :class="{ 'step-done': canRegister }">
                <Popover :triggers="['hover']" placement="top">
                  Provide sample information
                  <span class="icon hoverable-icon is-small has-text-primary">
                    <i class="fa fa-info fa-xs" />
                  </span>
                  <template #popper>
                    <p>
                      Each datasets can be associated to a
                      <code>new</code>
                      or to an existing sample
                    </p>
                    <ul>
                      <li class="pt-2">
                        <span class="tag is-primary p-1">new</span>
                        To assign a datasets to a new sample, just provide the
                        name as well as other sample info (
                        <em>e.g</em>
                        barcode for illumina library samples)
                      </li>
                      <li class="pt-2">
                        <span class="icon has-text-primary is-small">
                          <i class="fa-solid fa-database fa-sm" />
                        </span>
                        To assign a datasets to an existing sample
                        <ul>
                          <li>Load samples from the DB</li>
                          <li>Select the sample in the dropdown</li>
                        </ul>
                      </li>
                    </ul>
                    <p class="mt-4">
                      Studies and owner can also here be adjusted for each
                      dataset
                    </p>
                  </template>
                </Popover>
              </li>
              <li class="mr-2 nocheck">Submit</li>
            </ul>
          </div>
        </div>
        <fieldset :disabled="isRegistering" style="display: contents">
          <div
            class="buttons is-flex is-align-items-flex-start is-justify-content-space-between p-4"
          >
            <div
              class="is-flex is-align-items-center"
              style="gap: var(--space-2)"
            >
              <BaseButton
                variant="primary"
                :outlined="true"
                size="small"
                :disabled="isRegistering"
                @click="
                  () => {
                    cellIndex = -1
                    openSamplePicker('search')
                  }
                "
              >
                <template #icon><i class="fa-solid fa-vial" /></template>
                <span>Load samples</span>
                <span v-if="loadedSamples.length">
                  ({{ loadedSamples.length }})
                </span>
              </BaseButton>
              <BaseButton
                variant="primary"
                :outlined="true"
                size="small"
                :disabled="isRegistering"
                @click="
                  () => {
                    cellIndex = -1
                    openStudyPicker('search')
                  }
                "
              >
                <template #icon><i class="fa-solid fa-microscope" /></template>
                Load studies
              </BaseButton>
              <BaseButton
                v-if="useSampleGroups"
                v-tooltip="`Create sample group`"
                variant="primary"
                :outlined="true"
                size="small"
                :disabled="isRegistering"
                @click="isOpenCreateSampleGroupModal = true"
              >
                <template #icon>
                  <i class="fa-solid fa-arrows-to-dot" />
                </template>
                <span>Sample group</span>
                <span v-if="loadedGroups.length">
                  ({{ loadedGroups.length }})
                </span>
              </BaseButton>
              <ToggleSwitch
                v-model="allowPooledSamples"
                v-tooltip="
                  `Allow assigning the same sample to more than one dataset within
              a collection. Use when the same biomaterial was used to generate
              more than one dataset.`
                "
                label="Allow Sample Reuse"
                :color="
                  hasPooledSamples && !allowPooledSamples ? 'danger' : 'primary'
                "
                :disabled="isRegistering"
              />
            </div>
            <div class="is-flex is-align-items-center">
              <span
                v-if="loadedSamples.length"
                class="tag is-primary is-light mr-2"
              >
                {{ loadedSamples.length }} loaded
                {{ pluralize('sample', loadedSamples.length) }}
              </span>
              <span
                v-if="loadedGroups.length"
                class="tag is-primary is-light mr-2"
              >
                {{ loadedGroups.length }} sample
                {{ pluralize('group', loadedGroups.length) }}
              </span>
            </div>
          </div>
        </fieldset>
      </div>
    </template>
    <template #body>
      <fieldset :disabled="isRegistering" style="display: contents">
        <div style="height: 100%; overflow: auto">
          <slot name="notifications" />
          <slot
            name="body"
            :dropdown-options="dropdownOptions"
            :meta-sample="metaSample"
            :sample-identity-fields="sampleIdentityFields"
            :sample-options="sortedSampleOptions"
            :sample-groups="
              loadedGroups.reduce(
                (groupsById, g) => ({ ...groupsById, [g.id]: g }),
                {},
              )
            "
            :study-options="studyOptions"
            :open-study-picker="openStudyPicker"
            :toggle-sample-modal="() => openSamplePicker('search')"
          />
        </div>
        <ModalCard
          v-if="useSampleGroups && isOpenCreateSampleGroupModal"
          :title="`
            <span class='icon is-small mr-1'>
              <i class='fa-solid fa-arrows-to-dot fa-xs'></i>
            </span>
            <span>Create sample group (${sampleType})</span>
          `"
          :handle-close="() => (isOpenCreateSampleGroupModal = false)"
          width="95%"
        >
          <template #body>
            <CreateSampleGroup
              :sample-type="sampleType"
              @new-group="(g) => (newSampleGroup = g)"
            />
          </template>
          <template #footer>
            <BaseButton
              size="small"
              variant="danger"
              outlined
              icon="fa-solid fa-times"
              label="Cancel"
              @click="isOpenCreateSampleGroupModal = false"
            />
            <BaseButton
              size="small"
              variant="primary"
              outlined
              icon="fa-solid fa-arrows-to-dot"
              label="Create sample group"
              :disabled="!newSampleGroup"
              @click="
                () => {
                  addSampleGroup(newSampleGroup)
                  isOpenCreateSampleGroupModal = false
                }
              "
            />
          </template>
        </ModalCard>
        <RecordPickerModal
          v-model="isSamplePickerOpen"
          :initial-tab="samplePickerTab"
          app-name="stocks"
          model-name="samples"
          :model-type="sampleType"
          mode="both"
          vuex-prefix="registerassay-"
          @select="addSampleOptions"
        />
        <RecordPickerModal
          v-model="isStudyPickerOpen"
          :initial-tab="studyPickerTab"
          app-name="core"
          model-name="studies"
          @select="addStudyOptions"
        />
      </fieldset>
    </template>
    <template #footer>
      <slot name="footer" />
    </template>
  </StickyFooter>
</template>

<script>
import { uniqBy } from 'lodash-es'
import { mapMutations, mapGetters } from 'vuex'
import pluralize from 'pluralize'

import RecordPickerModal from '@/components/RecordPickerModal.vue'
import CreateSampleGroup from '@/components/data-loader/CreateSampleGroup.vue'
import StickyFooter from '@/components/data-loader/StickyFooter.vue'

import { useRecordPicker } from '@/composables/useRecordPicker'
import { get } from '@/api'

export default {
  name: 'AssignSamplesView',
  emits: ['update-allow-pooled-samples'],
  components: {
    CreateSampleGroup,
    StickyFooter,
    RecordPickerModal,
  },
  props: {
    groupId: {
      type: Number,
      default: null,
    },
    sampleType: {
      type: String,
      default: null,
    },
    canRegister: {
      type: Boolean,
      default: false,
    },
    isRegistering: {
      type: Boolean,
      default: false,
    },
    hasPooledSamples: {
      type: Boolean,
      default: false,
    },
    useSampleGroups: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const {
      isOpen: isSamplePickerOpen,
      initialTab: samplePickerTab,
      openPicker: openSamplePicker,
    } = useRecordPicker()
    const {
      isOpen: isStudyPickerOpen,
      initialTab: studyPickerTab,
      openPicker: openStudyPicker,
    } = useRecordPicker()

    return {
      isSamplePickerOpen,
      samplePickerTab,
      openSamplePicker,
      isStudyPickerOpen,
      studyPickerTab,
      openStudyPicker,
    }
  },
  data() {
    return {
      dropdownOptions: {},
      allowPooledSamples: false,
      isOpenCreateSampleGroupModal: false,
      loadedSamples: [],
      loadedGroups: [],
      newSampleGroup: null,
    }
  },
  computed: {
    ...mapGetters('meta', ['getMeta']),
    ...mapGetters('dataloader/main', ['studyOptions']),
    metaSample() {
      return this.getMeta('stocks', 'sample', this.sampleType)
    },
    sampleIdentityFields() {
      switch (this.sampleType) {
        case 'SEQUENCINGLIBRARY':
          return ['barcode']
        case 'GENERICSAMPLE':
        default:
          return []
      }
    },
    sortedSampleOptions() {
      let sliceOpts = this.sampleOptions.slice().sort((s1, s2) => {
        const name1 = s1._flatName
        const name2 = s2._flatName
        return name1.localeCompare(name2)
      })
      return sliceOpts
    },
    metaChoicesUrlOwner() {
      const ownerMeta = this.sampleFieldsMetaModel('owner')
      let ownerChoicesUrl = ownerMeta?.choices?.api?.url
      let userGroups = [this.groupId]
      ownerChoicesUrl = ownerChoicesUrl
        ? ownerChoicesUrl + `&group=${userGroups.join('&group=')}`
        : null
      return {
        key: 'owner',
        url: ownerChoicesUrl,
      }
    },
    metaChoicesUrlsSampleIdFields() {
      return this.sampleIdentityFields.map((key) => {
        const fieldMeta = this.sampleFieldsMetaModel(key)
        const fieldMetaChoiceUrl = fieldMeta?.choices?.api?.url
        return {
          key,
          url: fieldMetaChoiceUrl || null,
        }
      })
    },
    metaChoiceFieldsUrls() {
      return [this.metaChoicesUrlOwner, ...this.metaChoicesUrlsSampleIdFields]
    },
    sampleOptions() {
      return uniqBy([...this.loadedSamples, ...this.loadedGroups], '_flatId')
    },
  },
  methods: {
    pluralize,
    ...mapMutations('dataloader/main', {
      addStudyOptions: 'ADD_STUDY_OPTIONS',
    }),
    sampleFieldsMetaModel(name) {
      const ret = this.metaSample?.model?.attributes?.find(
        (x) => x.name === name,
      )
      return ret
    },
    addSampleOptions(samples) {
      const loadedSamples = samples.map((s) => ({
        ...s,
        _flatId: s.id.value,
        _flatName: s.name.value.name,
        choice_label: s.choice_label.value,
      }))
      this.loadedSamples = uniqBy(
        [...loadedSamples, ...this.loadedSamples],
        '_flatId',
      )
    },
    addSampleGroup(sampleGroup) {
      const loadedGroup = {
        ...sampleGroup,
        _flatId: sampleGroup.id,
        _flatName: `Group: ${sampleGroup.name}`,
      }
      this.loadedGroups = uniqBy([loadedGroup, ...this.loadedGroups], '_flatId')
      this.newSampleGroup = null
    },
    async getFieldOptions(fieldName, url) {
      let options = []
      const optionsResponse = await get(url)
      options = optionsResponse.data?.results || []
      return options
    },
  },
  watch: {
    allowPooledSamples: {
      handler: function (allowPooledSamples) {
        this.$emit('update-allow-pooled-samples', allowPooledSamples)
      },
      immediate: true,
    },
    metaChoiceFieldsUrls: {
      handler: function (fieldUrls) {
        fieldUrls.forEach(async ({ key, url }) => {
          if (url) {
            const opt = await this.getFieldOptions(key, url)
            this.dropdownOptions[key] = opt
          }
        })
      },
      immediate: true,
    },
  },
}
</script>
